import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, ShoppingBag } from "lucide-react";
import { PageHeader, PageBody, StatusPill, statusTone } from "@/components/PageChrome";
import { Button } from "@/components/controls";

export default function CustomerDetail() {
  const { phone } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get(`/customers/${encodeURIComponent(phone)}`)
      .then((r) => setProfile(r.data))
      .catch((e) => { toast.error(formatApiError(e)); navigate("/customers"); });
  }, [phone, navigate]);

  if (!profile) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <>
      <PageHeader title={profile.name || "Customer"} subtitle={profile.phone} testId="customer-detail-title">
        <Button onClick={() => navigate("/customers")}><ArrowLeft size={13} /> Back</Button>
      </PageHeader>
      <PageBody>
        <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="field-label">Contact</div>
            <div className="flex items-center gap-1.5 text-[13px] mt-1"><Phone size={13} className="text-slate-500" /> <span className="font-mono">{profile.phone}</span></div>
            {profile.email && <div className="flex items-center gap-1.5 text-[13px] mt-1"><Mail size={13} className="text-slate-500" /> <span className="font-mono">{profile.email}</span></div>}
            {profile.city && <div className="flex items-center gap-1.5 text-[13px] mt-1"><MapPin size={13} className="text-slate-500" /> {[profile.city, profile.state].filter(Boolean).join(", ")}</div>}
          </div>
          <div>
            <div className="field-label">Total Orders</div>
            <div className="font-mono text-2xl font-semibold">{profile.total_orders}</div>
            <div className="text-[11px] text-slate-500 font-mono">₹{profile.total_spend.toLocaleString()} lifetime spend</div>
          </div>
          <div>
            <div className="field-label">Return Acceptance</div>
            <div className={`font-mono text-2xl font-semibold ${profile.return_acceptance_rate >= 80 ? "text-emerald-700" : profile.return_acceptance_rate > 0 ? "text-amber-700" : "text-slate-700"}`}>
              {profile.returns_initiated ? `${profile.return_acceptance_rate}%` : "—"}
            </div>
            <div className="text-[11px] text-slate-500 font-mono">{profile.return_received}/{profile.returns_initiated} accepted</div>
          </div>
          <div>
            <div className="field-label">Risk Signals</div>
            <div className="text-[12.5px] mt-1">RTO Rate: <span className="font-mono font-semibold">{profile.rto_rate}%</span></div>
            <div className="text-[12.5px]">Refund Rate: <span className="font-mono font-semibold">{profile.refund_rate}%</span></div>
            <div className="text-[12.5px]">Marketplaces: {profile.marketplaces.join(", ") || "—"}</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
            <ShoppingBag size={14} className="text-slate-500" />
            <span className="font-heading text-[13px] font-semibold">Order History ({profile.orders.length})</span>
          </div>
          <table className="erp-table">
            <thead>
              <tr>
                <th>System ID</th><th>Purchase Date</th><th>Marketplace</th><th>Product</th>
                <th>Qty</th><th>Item ₹</th><th>Shipping</th><th>MP Status</th><th>Return</th>
              </tr>
            </thead>
            <tbody>
              {profile.orders.map((o) => (
                <tr key={o.id} onClick={() => navigate(`/orders/${o.id}`)} className="cursor-pointer">
                  <td className="font-mono">#{o.system_id}</td>
                  <td className="font-mono">{o.purchase_date || "—"}</td>
                  <td>{o.marketplace}</td>
                  <td className="truncate max-w-[220px]">{o.product_name}</td>
                  <td className="font-mono">{o.qty_purchased}</td>
                  <td className="font-mono">₹{Number(o.item_price || 0).toLocaleString()}</td>
                  <td><StatusPill value={o.shipping_status} tone={statusTone(o.shipping_status)} /></td>
                  <td><StatusPill value={o.marketplace_status} tone={statusTone(o.marketplace_status)} /></td>
                  <td><StatusPill value={o.return_received_status} tone={statusTone(o.return_received_status)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}
